import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { ContactForm } from "@/components/calv/ContactForm";
import { trackConversion } from "@/lib/analytics";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { EinzugsgebietMap } from "@/components/calv/EinzugsgebietMap";
import { QualificationCard } from "@/components/calv/QualificationCard";

export const Route = createFileRoute("/en/contact")({
  head: () => {
    const h = pageHead("contact", "en");
    return { meta: h.meta, links: h.links };
  },
  component: ContactPage,
});

const channels = [
  {
    label: "Email",
    value: "info@calvarius.com",
    href: "mailto:info@calvarius.com",
    detail: "Prompt reply, 1-2 business days",
  },
  {
    label: "Phone",
    value: "+49 9353 9768 000",
    href: "tel:+4993539768000",
    detail: "Mon – Fri · 9 am – 5 pm",
  },
];

function ContactPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Let's talk"
          headline={<>Get in <em>touch.</em></>}
          lead="Let's talk about how your online performance can be better managed economically."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Contact" }]}
        />

        <Section
          number="02"
          kicker="When a conversation makes sense"
          headline={<>First <em>assess,</em> then decide.</>}
          caption="An initial conversation is not about selling, but about assessment."
        >
          <RevealOnScroll>
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
              <QualificationCard
                tone="bright"
                eyebrow="What we clarify together"
                headline="We clarify together"
                items={[
                  "Where you currently stand",
                  "What goals you're pursuing",
                  "What challenges exist",
                  "If and how we can meaningfully support",
                ]}
                footer="Not every project fits us — and we're not the right partner for every project. That's exactly what we find out in an open conversation."
              />
              <QualificationCard
                tone="muted"
                eyebrow="Self-assessment"
                headline={<>A conversation makes sense if you&hellip;</>}
                items={[
                  "Actively run online marketing or e-commerce",
                  "Deploy or want to build relevant budgets",
                  "Value clean structures and clear decisions",
                  "Appreciate plain speaking and honest assessment",
                ]}
                footerEyebrow="…and less suitable when"
                footer="You're looking for quick implementation without strategic assessment. Then we're probably not the right contact."
              />
            </div>

            <div className="mx-auto mt-24 max-w-[880px] border-l-4 border-lime pl-8">
              <p className="font-display text-[clamp(24px,3vw,34px)] font-medium leading-[1.3] text-ink">
                Our goal is not to generate as many inquiries as possible. But to have{" "}
                <em className="italic font-medium text-ink-3">the right conversations.</em>
              </p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="03"
          kicker="Direct channels"
          headline={<>Write to us <em>directly.</em></>}
          caption="Use the form to briefly describe your concern. The more specific your information, the better we can prepare."
        >
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <RevealOnScroll>
              <div className="space-y-px overflow-hidden rounded-lg border border-rule bg-rule">
                {channels.map((c) => (
                  <div key={c.label} className="bg-white p-5">
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
                      {c.label}
                    </div>
                    <div className="mt-2 font-display text-lg text-ink">
                      <a
                        href={c.href}
                        onClick={() => {
                          if (c.href.startsWith("tel:")) trackConversion("phone_number_clicked", { location: "contact_page" });
                        }}
                        className="hover:text-cyan-deep"
                      >
                        {c.value}
                      </a>
                    </div>
                    <div className="mt-1 text-sm text-ink-3">{c.detail}</div>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-sm leading-[1.7] text-ink-3">
                The initial conversation is non-binding and serves mutual assessment. You'll
                receive honest feedback in any case – even if we don't see added value.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay={120}>
              <ContactForm />
            </RevealOnScroll>
          </div>
        </Section>

        <Section
          number="04"
          variant="tinted"
          kicker="Location"
          headline={<>Karlstadt am Main — and everywhere <em>mandates need us.</em></>}
        >
          <RevealOnScroll>
            <div className="mx-auto max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>
                Calvarius works from Karlstadt am Main, in the heart of Mainfranken. In-person meetings in the region are economically viable because Würzburg, Schweinfurt, Aschaffenburg, Frankfurt, and Nürnberg are within 25 to 100 kilometers. For mandates outside the region we work digitally — much of the ongoing management runs remotely anyway.
              </p>
              <p>
                If you prefer an in-person first conversation in our region: gladly. If a video call or phone is enough: equally so. We follow your preference, not ours.
              </p>
            </div>
            <EinzugsgebietMap locale="en" />
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="en" number="05" />
      </main>
      <Footer />
    </>
  );
}
